import { Box } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StatStrip, type StatStripItem } from "../../../../../components/UIToolkit/molecules/StatStrip";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import { SystemDataGrid } from "../../../../../components/UIToolkit/Tables/SystemDataGrid";
import { INTEGRATION_SUMMARY, NEUTRAL } from "../../../../../theme/tokens";
import type {
	CreatedEmployeeSummaryRow,
	DuplicateNurseDetail,
	DuplicateNurseGroup,
	IntegrationEmployeesSummary,
	IntegrationTimeOffSummary,
	UnmappedRoleStringSummaryRow,
	UnmappedTimeOffTypeSummaryRow,
} from "../../../../../utils/models";
import { configurationSectionSx, gridSx, sectionSx } from "./consts";
import { EmptySummaryState } from "./EmptySummaryState";
import { SectionHeading, sectionHeadingCountProps } from "./SectionHeading";

type Props = { summaryType: "employees"; summary: IntegrationEmployeesSummary } | { summaryType: "time_off"; summary: IntegrationTimeOffSummary };

type CountRow = { id: string; label: string; count: number };
type DuplicateGridRow = DuplicateNurseDetail & { normalizedName: string; rowId: string };

const CreatedEmployeesGrid: FC<{ section: IntegrationEmployeesSummary["createdEmployees"]; title: string; description: string }> = ({
	section,
	title,
	description,
}) => {
	const { t } = useTranslation();
	const columns = useMemo<GridColDef<CreatedEmployeeSummaryRow>[]>(
		() => [
			{ field: "fullName", headerName: t("integration.summary.column.employee"), flex: 1, minWidth: 150 },
			{ field: "role", headerName: t("integration.summary.column.role"), flex: 0.8, minWidth: 120 },
			{ field: "job", headerName: t("integration.summary.column.job"), flex: 0.8, minWidth: 120 },
			{ field: "roleString", headerName: t("integration.summary.column.roleString"), flex: 1, minWidth: 150 },
			{ field: "employmentType", headerName: t("integration.summary.column.employmentType"), flex: 0.9, minWidth: 130 },
			{ field: "source", headerName: t("integration.summary.column.source"), flex: 0.7, minWidth: 110 },
			{ field: "createdAt", headerName: t("integration.summary.column.createdAt"), flex: 1, minWidth: 165 },
			{
				field: "active",
				headerName: t("integration.summary.column.active"),
				flex: 0.55,
				minWidth: 85,
				valueGetter: (_value, row) => t(row.active ? "integration.summary.status.active" : "integration.summary.status.inactive"),
			},
		],
		[t],
	);
	return (
		<>
			<SectionHeading title={title} description={description} />
			<Box sx={gridSx} data-testid="hris-employees-summary-imported-grid">
				<SystemDataGrid<CreatedEmployeeSummaryRow>
					rows={section.rows}
					columns={columns}
					getRowId={(row) => row.externalEmployeeId}
					dataTestId="hris-employees-summary-imported"
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					emptyState={
						<EmptySummaryState
							title={t("integration.summary.employees.noRows.title")}
							hint={t("integration.summary.employees.noRows.hint")}
							testId="hris-employees-summary-imported-empty"
						/>
					}
				/>
			</Box>
		</>
	);
};

const UnmappedRolesGrid: FC<{
	section: IntegrationEmployeesSummary["unmappedRoleStrings"];
	title: string;
	description: string;
}> = ({ section, title, description }) => {
	const { t } = useTranslation();
	const columns = useMemo<GridColDef<UnmappedRoleStringSummaryRow>[]>(
		() => [
			{ field: "roleString", headerName: t("integration.summary.column.roleString"), flex: 1, minWidth: 155 },
			{ field: "employeeCount", headerName: t("integration.summary.column.employeeCount"), width: 110 },
			{
				field: "sampleEmployeeNames",
				headerName: t("integration.summary.column.sampleNames"),
				flex: 1.2,
				minWidth: 190,
				valueGetter: (_value, row) => row.sampleEmployeeNames.join(", "),
			},
			{
				field: "internalDepartments",
				headerName: t("integration.summary.column.internalDepartments"),
				flex: 1,
				minWidth: 165,
				valueGetter: (_value, row) => row.internalDepartments.join(", "),
			},
		],
		[t],
	);
	return (
		<>
			<SectionHeading title={title} description={description} {...sectionHeadingCountProps(section)} />
			<Box sx={gridSx} data-testid="hris-employees-summary-unmapped-roles-grid">
				<SystemDataGrid<UnmappedRoleStringSummaryRow>
					rows={section.rows}
					columns={columns}
					getRowId={(row) => row.roleString}
					dataTestId="hris-employees-summary-unmapped-roles"
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					emptyState={
						<EmptySummaryState
							title={t("integration.summary.employees.noUnmappedRoles.title")}
							hint={t("integration.summary.employees.noUnmappedRoles.hint")}
							testId="hris-employees-summary-unmapped-roles-empty"
						/>
					}
				/>
			</Box>
		</>
	);
};

const DuplicateGrid: FC<{
	section: IntegrationEmployeesSummary["duplicateNursesSameDepartment"];
	testId: string;
	showNormalizedName: boolean;
	title: string;
	description: string;
	emptyTitle: string;
	emptyHint: string;
}> = ({ section, testId, showNormalizedName, title, description, emptyTitle, emptyHint }) => {
	const { t } = useTranslation();
	const rows = useMemo<DuplicateGridRow[]>(
		() =>
			section.rows.flatMap((group: DuplicateNurseGroup) =>
				group.nurses.map((nurse) => ({ ...nurse, normalizedName: group.normalizedName, rowId: `${group.normalizedName}-${nurse.nurseId}` })),
			),
		[section.rows],
	);
	const columns = useMemo<GridColDef<DuplicateGridRow>[]>(
		() => [
			...(showNormalizedName ? [{ field: "normalizedName", headerName: t("integration.summary.column.normalizedName"), flex: 1, minWidth: 155 }] : []),
			{ field: "fullName", headerName: t("integration.summary.column.name"), flex: 1, minWidth: 145 },
			{ field: "nurseId", headerName: t("integration.summary.column.nurseId"), width: 90 },
			{
				field: "departmentNames",
				headerName: t("integration.summary.column.departments"),
				flex: 1.15,
				minWidth: 175,
				valueGetter: (_value, row) => row.departmentNames.join(", "),
			},
			{ field: "actualShiftCount", headerName: t("integration.summary.column.actualShifts"), width: 105 },
			{ field: "scheduledShiftCount", headerName: t("integration.summary.column.scheduledShifts"), width: 115 },
			{
				field: "hasExternalEmployee",
				headerName: t("integration.summary.column.integrationRecord"),
				width: 125,
				valueGetter: (_value, row) => t(row.hasExternalEmployee ? "integration.summary.status.yes" : "integration.summary.status.no"),
			},
			{
				field: "hasEmail",
				headerName: t("integration.summary.column.email"),
				width: 90,
				valueGetter: (_value, row) => t(row.hasEmail ? "integration.summary.status.yes" : "integration.summary.status.no"),
			},
			{
				field: "hasPhone",
				headerName: t("integration.summary.column.phone"),
				width: 90,
				valueGetter: (_value, row) => t(row.hasPhone ? "integration.summary.status.yes" : "integration.summary.status.no"),
			},
		],
		[showNormalizedName, t],
	);
	return (
		<>
			<SectionHeading title={title} description={description} {...sectionHeadingCountProps(section)} />
			<Box sx={gridSx} data-testid={`${testId}-grid`}>
				<SystemDataGrid<DuplicateGridRow>
					rows={rows}
					columns={columns}
					getRowId={(row) => row.rowId}
					dataTestId={testId}
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					emptyState={<EmptySummaryState title={emptyTitle} hint={emptyHint} testId={`${testId}-empty`} />}
				/>
			</Box>
		</>
	);
};

const CountGrid: FC<{
	rows: CountRow[];
	testId: string;
	title?: string;
	description?: string;
	countSection?: { total: number; rows: readonly unknown[]; truncated: boolean };
	emptyTitle: string;
	emptyHint: string;
}> = ({ rows, testId, title, description, countSection, emptyTitle, emptyHint }) => {
	const { t } = useTranslation();
	const columns = useMemo<GridColDef<CountRow>[]>(
		() => [
			{ field: "label", headerName: t("integration.summary.column.type"), flex: 1, minWidth: 180 },
			{ field: "count", headerName: t("integration.summary.column.count"), width: 120 },
		],
		[t],
	);
	return (
		<>
			{title &&
				description &&
				(countSection ? (
					<SectionHeading title={title} description={description} {...sectionHeadingCountProps(countSection)} />
				) : (
					<SectionHeading title={title} description={description} />
				))}
			<Box sx={gridSx} data-testid={`${testId}-grid`}>
				<SystemDataGrid<CountRow>
					rows={rows}
					columns={columns}
					getRowId={(row) => row.id}
					dataTestId={testId}
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					emptyState={<EmptySummaryState title={emptyTitle} hint={emptyHint} testId={`${testId}-empty`} />}
				/>
			</Box>
		</>
	);
};

const UnmappedTimeOffGrid: FC<{
	section: IntegrationTimeOffSummary["unmappedTimeOffTypes"];
	title: string;
	description: string;
}> = ({ section, title, description }) => {
	const { t } = useTranslation();
	const columns = useMemo<GridColDef<UnmappedTimeOffTypeSummaryRow>[]>(
		() => [
			{ field: "rawString", headerName: t("integration.summary.column.rawType"), flex: 1, minWidth: 220 },
			{ field: "countLast60", headerName: t("integration.summary.column.countLast60"), width: 160 },
		],
		[t],
	);
	return (
		<>
			<SectionHeading title={title} description={description} {...sectionHeadingCountProps(section)} />
			<Box sx={gridSx} data-testid="hris-time-off-summary-unmapped-grid">
				<SystemDataGrid<UnmappedTimeOffTypeSummaryRow>
					rows={section.rows}
					columns={columns}
					getRowId={(row) => row.rawString}
					dataTestId="hris-time-off-summary-unmapped"
					containedHeight={{ maxHeight: INTEGRATION_SUMMARY.GRID_HEIGHT }}
					emptyState={
						<EmptySummaryState
							title={t("integration.summary.timeoff.noUnmapped.title")}
							hint={t("integration.summary.timeoff.noUnmapped.hint")}
							testId="hris-time-off-summary-unmapped-empty"
						/>
					}
				/>
			</Box>
		</>
	);
};

const EmployeesContent: FC<{ summary: IntegrationEmployeesSummary }> = ({ summary }) => {
	const { t } = useTranslation();
	const importedStats: StatStripItem[] = [
		{ label: t("integration.summary.employees.imported"), value: summary.createdCount, emphasis: true, dataTestId: "hris-employees-summary-imported-count" },
		{ label: t("integration.summary.employees.activeCreated"), value: summary.activeCreatedCount, dataTestId: "hris-employees-summary-active-created-count" },
	];
	return (
		<>
			<Box sx={(theme) => sectionSx(theme)} data-testid="hris-employees-summary-imported-section">
				<SectionHeading title={t("integration.summary.employees.imported.title")} description={t("integration.summary.employees.imported.description")} />
				<StatStrip items={importedStats} variant="band" dataTestId="hris-employees-summary-imported-stats" />
				{summary.createdCount === 0 ? (
					<EmptySummaryState
						title={summary.hasEverSynced ? t("integration.summary.imported.empty.title") : t("integration.summary.notRunYet.title")}
						hint={summary.hasEverSynced ? t("integration.summary.imported.empty.hint") : t("integration.summary.notRunYet.hint")}
						testId="hris-employees-summary-imported-empty"
					/>
				) : (
					<CreatedEmployeesGrid
						section={summary.createdEmployees}
						title={t("integration.summary.employees.imported.title")}
						description={t("integration.summary.employees.imported.description")}
					/>
				)}
			</Box>
			<Box sx={(theme) => configurationSectionSx(theme)} data-testid="hris-employees-summary-configuration-section">
				<SystemBanner
					tone="warning"
					title={t("integration.summary.configuration.title")}
					description={t("integration.summary.configuration.description")}
					dataTestId="hris-employees-summary-configuration-banner"
				/>
				<StatStrip
					items={[{ label: t("integration.summary.employees.activeHris"), value: summary.activeHrisEmployeesTotal, emphasis: true }]}
					variant="inline"
					dataTestId="hris-employees-summary-active-hris"
				/>
				<UnmappedRolesGrid
					section={summary.unmappedRoleStrings}
					title={t("integration.summary.employees.unmappedRoles.title")}
					description={t("integration.summary.employees.unmappedRoles.description")}
				/>
				<DuplicateGrid
					section={{
						total: summary.nursesWithoutIntegration.total,
						truncated: summary.nursesWithoutIntegration.truncated,
						rows: summary.nursesWithoutIntegration.rows.map((row) => ({ normalizedName: row.fullName, nurses: [row] })),
					}}
					testId="hris-employees-summary-no-integration"
					showNormalizedName={false}
					title={t("integration.summary.employees.noIntegration.title")}
					description={t("integration.summary.employees.noIntegration.description")}
					emptyTitle={t("integration.summary.employees.noIntegration.empty.title")}
					emptyHint={t("integration.summary.employees.noIntegration.empty.hint")}
				/>
				<DuplicateGrid
					section={summary.duplicateNursesSameDepartment}
					testId="hris-employees-summary-duplicates-department"
					showNormalizedName
					title={t("integration.summary.employees.sameDepartment.title")}
					description={t("integration.summary.employees.sameDepartment.description")}
					emptyTitle={t("integration.summary.employees.noDuplicates.title")}
					emptyHint={t("integration.summary.employees.noDuplicates.hint")}
				/>
				<DuplicateGrid
					section={summary.duplicateNursesSameNetwork}
					testId="hris-employees-summary-duplicates-network"
					showNormalizedName
					title={t("integration.summary.employees.sameNetwork.title")}
					description={t("integration.summary.employees.sameNetwork.description")}
					emptyTitle={t("integration.summary.employees.noDuplicates.title")}
					emptyHint={t("integration.summary.employees.noDuplicates.hint")}
				/>
			</Box>
		</>
	);
};

const TimeOffContent: FC<{ summary: IntegrationTimeOffSummary }> = ({ summary }) => {
	const { t } = useTranslation();
	const importedRows = useMemo<CountRow[]>(
		() => Object.entries(summary.createdByType).map(([label, count]) => ({ id: label, label, count })),
		[summary.createdByType],
	);
	const manualRows = useMemo<CountRow[]>(
		() => Object.entries(summary.manualTimeOffBySource).map(([label, count]) => ({ id: label, label, count })),
		[summary.manualTimeOffBySource],
	);
	const manualEmployeeRows = useMemo<CountRow[]>(
		() => summary.manualTimeOffByEmployee.rows.map((row) => ({ id: String(row.nurseId), label: row.fullName, count: row.count })),
		[summary.manualTimeOffByEmployee.rows],
	);
	return (
		<>
			<Box sx={(theme) => sectionSx(theme)} data-testid="hris-time-off-summary-imported-section">
				<SectionHeading title={t("integration.summary.timeoff.imported.title")} description={t("integration.summary.timeoff.imported.description")} />
				<StatStrip
					items={[
						{ label: t("integration.summary.timeoff.imported"), value: summary.createdCount, emphasis: true },
						{ label: t("integration.summary.timeoff.paid"), value: summary.paidCount },
						{ label: t("integration.summary.timeoff.unpaid"), value: summary.unpaidCount },
						{ label: t("integration.summary.timeoff.linked"), value: summary.linkedToScheduleCount },
					]}
					variant="band"
					dataTestId="hris-time-off-summary-imported-stats"
				/>
				<CountGrid
					rows={importedRows}
					testId="hris-time-off-summary-by-type"
					emptyTitle={summary.hasEverSynced ? t("integration.summary.imported.empty.title") : t("integration.summary.notRunYet.title")}
					emptyHint={summary.hasEverSynced ? t("integration.summary.imported.empty.hint") : t("integration.summary.notRunYet.hint")}
				/>
			</Box>
			<Box sx={(theme) => configurationSectionSx(theme)} data-testid="hris-time-off-summary-configuration-section">
				<SystemBanner
					tone="warning"
					title={t("integration.summary.configuration.title")}
					description={t("integration.summary.configuration.description")}
					dataTestId="hris-time-off-summary-configuration-banner"
				/>
				<UnmappedTimeOffGrid
					section={summary.unmappedTimeOffTypes}
					title={t("integration.summary.timeoff.unmapped.title")}
					description={t("integration.summary.timeoff.unmapped.description")}
				/>
				<SectionHeading title={t("integration.summary.timeoff.manual.title")} description={t("integration.summary.timeoff.manual.description")} />
				<StatStrip
					items={[{ label: t("integration.summary.timeoff.manualLast30"), value: summary.manualTimeOffLast30Count, emphasis: true }]}
					variant="inline"
					dataTestId="hris-time-off-summary-manual-count"
				/>
				<CountGrid
					rows={manualRows}
					testId="hris-time-off-summary-manual-by-source"
					title={t("integration.summary.timeoff.manual.title")}
					description={t("integration.summary.timeoff.manual.description")}
					emptyTitle={t("integration.summary.timeoff.noManual.title")}
					emptyHint={t("integration.summary.timeoff.noManual.hint")}
				/>
				<CountGrid
					rows={manualEmployeeRows}
					testId="hris-time-off-summary-manual-by-employee"
					title={t("integration.summary.timeoff.manualByEmployee.title")}
					description={t("integration.summary.timeoff.manualByEmployee.description")}
					countSection={summary.manualTimeOffByEmployee}
					emptyTitle={t("integration.summary.timeoff.noManual.title")}
					emptyHint={t("integration.summary.timeoff.noManual.hint")}
				/>
			</Box>
		</>
	);
};

export const IntegrationSummaryContent: FC<Props> = ({ summaryType, summary }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 2, color: NEUTRAL[900] }} data-testid={`hris-${summaryType.replace("_", "-")}-summary-content`}>
		{summaryType === "employees" ? <EmployeesContent summary={summary} /> : <TimeOffContent summary={summary} />}
	</Box>
);
